import { OktonAuthPage, OktonInput } from '@okton/platform-ui';
import React, { useState } from 'react';
import {
  Button,
  Flex,
  HStack,
  Icon,
  Link as ChakraLink,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';

import { Link, useHistory } from 'react-router-dom';

import {
  LuArrowLeft,
  LuArrowRight,
  LuAtSign,
  LuCheck,
  LuCircleUserRound,
  LuKeyRound,
} from 'react-icons/lu';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { SubmitHandler, useForm } from 'react-hook-form';

import api from '../../services/api';

const createAccountFormSchema = z
  .object({
    email: z.string().nonempty('E-mail obrigatório').email('E-mail inválido'),
    nomeUsuario: z.string().nonempty('Nome obrigatório'),
    nomeEmpresa: z.string().nonempty('Empresa obrigatória'),
    nomeFilial: z.string().nonempty('Filial obrigatória'),
    cnpjEmpresa: z.string().nonempty('CNPJ obrigatório'),
    password: z
      .string()
      .nonempty('Senha obrigatória')
      .min(6, 'No mínimo 6 caracteres'),
    passwordConfirmation: z
      .string()
      .nonempty('Confirmação de senha obrigatória')
      .min(6, 'No mínimo 6 caracteres'),
  })
  .refine(data => data.password === data.passwordConfirmation, {
    message: 'As senhas devem ser iguais.',
    path: ['passwordConfirmation'],
  });

type CreateAccountFormData = z.infer<typeof createAccountFormSchema>;

const ENDPOINT_CADASTRO_EMPRESA =
  process.env.REACT_APP_AUTH_REGISTER_COMPANY_ENDPOINT ||
  '/auth/register-company';

const CreateAccount: React.FC = () => {
  const [step, setStep] = useState(1);

  const { register, handleSubmit, formState, trigger } =
    useForm<CreateAccountFormData>({
      resolver: zodResolver(createAccountFormSchema),
    });

  const { isSubmitting, errors } = formState;

  const color = useColorModeValue('gray.700', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const activeStepBg = useColorModeValue('blue.400', 'blue.400');
  const inactiveStepBg = useColorModeValue('gray.500', 'gray.500');

  const history = useHistory();
  const toast = useToast();

  const handleNextStep = async () => {
    const isValid = await trigger([
      'nomeUsuario',
      'cnpjEmpresa',
      'nomeEmpresa',
      'nomeFilial',
    ]);

    if (isValid) {
      setStep(2);
    }
  };

  const handleCreateAccount: SubmitHandler<CreateAccountFormData> = async ({
    email,
    nomeUsuario,
    nomeEmpresa,
    nomeFilial,
    cnpjEmpresa,
    password,
    passwordConfirmation,
  }) => {
    try {
      await api.post(ENDPOINT_CADASTRO_EMPRESA, {
        usuario: {
          nome: nomeUsuario,
          email,
          password,
          passwordConfirmation,
        },
        empresa: {
          nome: nomeEmpresa,
          cnpj: cnpjEmpresa,
        },
        filial: {
          nome: nomeFilial,
        },
      });

      toast({
        title: 'Cadastro realizado com sucesso.',
        description: 'Agora faça login para selecionar empresa e filial.',
        status: 'success',
        duration: 7000,
        isClosable: true,
      });

      history.push('/login');
    } catch (error: unknown) {
      const mensagemErro =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || 'Erro na criação da conta';

      toast({
        title: mensagemErro,
        description:
          'Ocorreu um erro ao criar empresa e filial. Tente novamente.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  return (
    <OktonAuthPage
      title="Criar a sua conta"
      onSubmit={handleSubmit(handleCreateAccount)}
      footer={
        <HStack justify="center">
          <Link to="/login">
            <ChakraLink as="div" color="blue.500" fontSize="sm">
              Já tem uma conta da Okton? Entrar
            </ChakraLink>
          </Link>
        </HStack>
      }
    >
      <Stack spacing="4">
        <HStack justify="space-between" py="6" spacing="4">
          <HStack spacing="3">
            <Flex
              w="50px"
              h="50px"
              rounded="full"
              align="center"
              justify="center"
              bg={activeStepBg}
              color="white"
              fontSize="xl"
            >
              {step > 1 ? <Icon as={LuCheck} /> : '1'}
            </Flex>

            <Text color={color} fontWeight="bold">
              Dados
            </Text>
          </HStack>

          <HStack spacing="3">
            <Flex
              w="50px"
              h="50px"
              rounded="full"
              align="center"
              justify="center"
              bg={step === 2 ? activeStepBg : inactiveStepBg}
              color={step === 2 ? 'white' : 'gray.900'}
              fontSize="xl"
            >
              2
            </Flex>

            <Text color={step === 2 ? color : mutedColor} fontWeight="bold">
              Acesso
            </Text>
          </HStack>
        </HStack>

        {step === 1 && (
          <>
            <OktonInput
              type="text"
              label="Digitar nome completo*"
              error={errors.nomeUsuario}
              leftIcon={LuCircleUserRound}
              {...register('nomeUsuario')}
            />

            <OktonInput
              type="text"
              label="CNPJ da empresa*"
              error={errors.cnpjEmpresa}
              leftIcon={LuCircleUserRound}
              {...register('cnpjEmpresa')}
            />

            <OktonInput
              type="text"
              label="Nome da empresa*"
              error={errors.nomeEmpresa}
              leftIcon={LuCircleUserRound}
              {...register('nomeEmpresa')}
            />

            <OktonInput
              type="text"
              label="Nome da filial (matriz)*"
              error={errors.nomeFilial}
              leftIcon={LuCircleUserRound}
              {...register('nomeFilial')}
            />
          </>
        )}

        {step === 2 && (
          <>
            <OktonInput
              type="email"
              label="Digitar email para login*"
              error={errors.email}
              leftIcon={LuAtSign}
              {...register('email')}
            />

            <OktonInput
              type="password"
              label="Digitar a senha*"
              error={errors.password}
              leftIcon={LuKeyRound}
              {...register('password')}
            />

            <OktonInput
              type="password"
              label="Confirmar a senha*"
              error={errors.passwordConfirmation}
              leftIcon={LuKeyRound}
              {...register('passwordConfirmation')}
            />

            <Text color={color} fontSize="xs" fontWeight="light">
              Ao me inscrever, aceito os{' '}
              <Link to="/">
                <ChakraLink
                  as="span"
                  color="blue.600"
                  fontSize="xs"
                  fontWeight="light"
                >
                  Termos de serviço da Okton
                </ChakraLink>
              </Link>{' '}
              e concordo com a{' '}
              <Link to="/">
                <ChakraLink
                  as="span"
                  color="blue.600"
                  fontSize="xs"
                  fontWeight="light"
                >
                  Política de Privacidade
                </ChakraLink>
              </Link>
              .
            </Text>
          </>
        )}
      </Stack>

      {step === 1 ? (
        <Button
          type="button"
          mt="6"
          rightIcon={<Icon as={LuArrowRight} />}
          onClick={handleNextStep}
        >
          Avançar
        </Button>
      ) : (
        <HStack mt="6" spacing="2">
          <Button
            type="button"
            variant="outline"
            flex="1"
            leftIcon={<Icon as={LuArrowLeft} />}
            onClick={() => setStep(1)}
          >
            Voltar
          </Button>

          <Button type="submit" flex="1" isLoading={isSubmitting}>
            Registre-se
          </Button>
        </HStack>
      )}
    </OktonAuthPage>
  );
};

export default CreateAccount;
